<template>
    <div>
        <template v-if="value && value.length > 0">
            <div
                v-for="(item, i) in value"
                :key="item"
            >
                <span>
                    {{ GetDisplayName(item) }}
                    <template v-if="i > 0"> ,</template>
                </span>
            </div>
        </template>
        <template v-else>
            <div>
                <GridButton
                    v-if="!isSaving"
                    @click="addContestant"
                    text="gpioreadings.addcontestant"
                    :row="row"
                    icon="fa-user-plus"
                />
                <span v-else>
                    <IconSpinner
                        :showSuccess="true"
                        :state="LoadState"
                    />
                </span>
            </div>
        </template>
    </div>
</template>
<script setup lang="ts">
import { GridButton, IconSpinner } from '@/components';
import GlobalsearchView from '@/components/Globalsearch/GlobalsearchView.vue';
import type { IGridCompExposed } from '@/components/Grid/Classes';
import { useTaskLoader, useTransactions } from '@/composeables';
import { useDialogBuilder } from '@/composeables/dialog/dialogbuilder';
import { GPIOReadingDao } from '@/dao';
import { AddTimeRequest, type ContestantBase, type GPIOReading, type TypeAheadItem } from '@/objects/generated';
import { useContestantStore, useToastStore } from '@/stores';
import { ThreadSleep } from '@/utilities/utils';
import { RowApiModule } from 'ag-grid-community';
import { ref } from 'vue';

const props = defineProps<{
    value?: Array<number>;

    row: GPIOReading;

    /**
     * The grid instance. We dont care about the type here
     */
    grid: IGridCompExposed<GPIOReading>;
}>();

const { GetDisplayName } = useContestantStore();

const { AddLoader, LoadState } = useTaskLoader();

let dialog: any | null = null;

const isSaving = ref(false);

/**
 * Opens the globalsearch dialog to add a contestant to the GPIO reading
 */
const addContestant = async () => {
    const model = ref<TypeAheadItem | null>();

    dialog = await useDialogBuilder()
        .withTitle('globalsearch.title')
        .withBodyComponent(GlobalsearchView, {
            vmodel: model,
            componentProps: {
                onItemSelected: handleSelectedSearchItem,
            },
        })
        .withCloseButton({ action: () => {} })
        .withOkButton({
            loadstate: LoadState,
            action: () => {
                if (!model.value) {
                    return false;
                }

                handleSelectedSearchItem(model.value);

                return true;
            },
        })
        .withOptions({
            Size: 'md',
            DisableAnimation: true,
        })
        .Open();
};

/**
 * When we receive a selected item from the globalsearch, we want to add it to the GPIO reading and close the dialog
 * @param item
 */
const handleSelectedSearchItem = async (item: TypeAheadItem) => {
    if (item.DataType != 'Contestant') {
        return;
    }

    const contestant: ContestantBase | undefined = useContestantStore().GetContestantByRaceContestantId(item.ID);
    if (!contestant || contestant.Bib == null) {
        useToastStore().addError('globalsearch.contestantnotfoundornobib');
        return;
    }

    dialog.closeDialog();
    isSaving.value = true;

    await AddLoader(async () => {
        // We fire the actual transaction to update the database, we do this after closing the dialog to make it feel more snappy
        try {
            const request = AddTimeRequest.FromJsValues({
                Bib: contestant.Bib,
                Time: props.row.Time,
                StationID: props.row.StationID,
                ContestantID: contestant.ID,
            });

            await useTransactions().AddTIM([request]);
        } catch (e) {
            useToastStore().addError('gpioreadings.erroraddingcontestant');
            console.error(e);
        } finally {
            isSaving.value = false;
        }
    }, '');
};
</script>
